import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const STORAGE_KEY = 'amazonStatusCache';
// Cached status is always revalidated on mount unless it was checked this recently.
// Short window: the connection can be dropped from another tab, another device or
// Seller Central, and nothing tells this tab about it — so a cached "connected" must
// never outlive it by more than one navigation.
const REVALIDATE_AFTER_MS = 30 * 1000;

// Module-level cache so concurrent consumers share one in-flight request
let cachedStatus = null;
let cachedPromise = null;
let cachedToken = null;
let cachedAt = 0;

// Invalidate all cached status (module + persisted). Call after any event that
// changes the connection state so the next read revalidates from the server
// instead of serving a stale value.
export const clearAmazonStatusCache = () => {
  cachedStatus = null;
  cachedPromise = null;
  cachedToken = null;
  cachedAt = 0;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort — ignore
  }
};

// Returning from the Amazon OAuth redirect (?amazon_connected=…) means any
// persisted status predates the connection and is stale. Purge it once at module
// load — which happens on the post-redirect full page load, before any component
// reads the cache — so the first status read fetches fresh and the UI doesn't
// stay on "Connect Amazon" after a successful connect.
try {
  if (typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('amazon_connected')) {
    localStorage.removeItem(STORAGE_KEY);
  }
} catch {
  // best-effort — ignore
}

// Persist the last status to localStorage so a page refresh can show it instantly
// (stale-while-revalidate) instead of waiting on the network. Keyed by token.
// Returns { data, ts } for the matching token, or null.
const readPersisted = (token) => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.token === token ? { data: parsed.data, ts: parsed.ts || 0 } : null;
  } catch {
    return null;
  }
};

const writePersisted = (token, data) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ token, data, ts: Date.now() }));
  } catch {
    // best-effort — ignore quota / serialization errors
  }
};

const fetchAmazonStatus = async (token) => {
  const response = await fetch(`${API_URL}/auth/amazon/status`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch Amazon status: ${response.status}`);
  }

  return response.json();
};

export const useAmazonStatus = (token) => {
  // Seed from the session cache, else from localStorage (survives refresh) — so we can
  // render the real connection state immediately instead of a loading/"not connected" flash.
  const persistedSeed = readPersisted(token);
  const seed = cachedStatus || (persistedSeed ? persistedSeed.data : null);
  const [data, setData] = useState(seed);
  const [loading, setLoading] = useState(!seed);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      cachedToken = null;
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    // If the logged-in user/session changed, invalidate the module cache
    if (cachedToken !== token) {
      cachedToken = token;
      cachedStatus = null;
      cachedPromise = null;
      cachedAt = 0;
    }

    // Adopt the persisted copy when this session hasn't fetched yet, so a refresh
    // renders the real state instantly instead of flashing "not connected".
    if (!cachedStatus) {
      const persisted = readPersisted(token);
      if (persisted) {
        cachedStatus = persisted.data;
        cachedAt = persisted.ts || 0;
      }
    }

    if (cachedStatus) {
      setData(cachedStatus);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError(null);

    // Cached value is shown while it revalidates; only a very recent check skips
    // the network entirely. A disconnect made elsewhere surfaces on the next mount.
    if (cachedStatus && Date.now() - cachedAt < REVALIDATE_AFTER_MS) return;

    // If a fetch is already in progress, reuse its promise
    if (!cachedPromise) {
      cachedPromise = fetchAmazonStatus(token)
        .then((result) => {
          cachedStatus = result;
          cachedAt = Date.now();
          writePersisted(token, result);
          return result;
        })
        .finally(() => {
          cachedPromise = null;
        });
    }

    let active = true;
    cachedPromise
      .then((result) => {
        if (active) {
          setData(result);
          setError(null);
        }
      })
      .catch((err) => {
        // Keep showing the persisted value on error instead of dropping to null.
        if (active) setError(err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [token]);

  const connected = !!(data && data.connected === true);

  return {
    data,
    connected,
    loading,
    error,
  };
};
